import { DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { CalendarMonthViewDay } from 'angular-calendar';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import { DATE_ISO_FORMAT } from '../../../constants/date.constants';
import { TRANSLATION_TEMPLATES } from '../../../constants/translation-templates-constants';
import { APPOINTMENT_CONSTANT } from '../../../constants/appointment-constants';
import { APPOINTMENT_SOURCE_ID, BOOKING_CUSTOM_FIELD_TYPES, BOOKING_REDIRECTION_VIEWS, GTM_EVENTS, PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS, PARENT_TO_CHILD_IFRAME_EVENTS, PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME, PAYMENT_TYPES, WIDGET_CONSTANTS, WIDGET_DISPLAY_TIMEZONE_SOURCE, WINDOW_LISTENER_CONSTANTS } from '../../../constants/widget-constants';
import { CustomFieldsDao } from '../../../db-models/appointment-custom-fields-dao';
import { AppointmentBookItem, CartItem } from '../../../models/cart.model';
import { CouponDbModel } from '../../../db-models/coupon-db.model';
import { PayPalOrderDetailModel, PayPalSettingsDbModel } from '../../../db-models/partner-setting.model';
import { AdyenPaymentDetails } from '../../../models/adyen.model';
import { AdyenResumeFailure, AdyenResumeReturn, AdyenResumeSnapshot } from '../../../models/adyen-resume.model';
import { AdyenResumeService } from '../../../services/adyen-resume.service';
import { buildAdyenHoldScope } from '../../../services/adyen-booking-context.util';
import { canBookAfterAdyenPayment } from '../../../services/adyen-payment-booking.util';
import { SaferPayPaymentDetailsDbModel } from '../../../models/saferpay.model';
import { PreDefinedSlotDao } from '../../../db-models/pre-defined-slot-dao';
import { PreDefinedSlotServiceCategoryGroup, PreDefinedSlotServiceDao } from '../../../db-models/pre-defined-slot-service-dao';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { GlobalObjects, Partner } from '../../../models/global';
import { UserState } from '../../../models/state.model';
import { WidgetColorConf } from '../../../models/widget-color.model';
import { CustomerPrefillDataModel, WidgetConf } from '../../../models/widget-conf';
import { DateUtilsService } from '../../../services/date-utils.service';
import { FormsService } from '../../../services/forms.service';
import { HelperService } from '../../../services/helper.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { LoggerService } from '../../../services/logger.service';
import { PartnerService } from '../../../services/partner.service';
import { PreDefinedSlotService } from '../../../services/pre-defined-slot.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { PaymentService } from '../../../services/payment.service';
import { UtilService } from '../../../services/util.service';
import { TimezoneService } from '../../../services/timezone.service';
import { WidgetUtilService } from '../../../services/widget-util.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { FinalpageComponent } from '../../../shared/components/finalpage/finalpage.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NewAlertComponent } from '../../../shared/components/new-alert/new-alert.component';
import { NgxClampComponent } from '../../../shared/components/ngx-clamp/ngx-clamp.component';
import { TranslationPipe } from '../../../shared/pipes/translation.pipe';
import { CwPaymentMethodsComponent } from '../../common/cw-payment-methods/cw-payment-methods.component';
import { PersonalDataFormComponent } from '../../common/personal-data-form/personal-data-form.component';
import { CwCardHeaderComponent } from '../../common/theme/cw-card-header/cw-card-header.component';
import { CwNewCardComponent } from '../../common/theme/cw-new-card/cw-new-card.component';
import { CwTitleComponent } from '../../common/theme/cw-title/cw-title.component';
import { CalioCurrencyPipe } from '../../../shared/pipes/calio-currency.pipe';
import { TrustHtmlPipe } from '../../../shared/pipes/trust-html.pipe';
import { CalendarPickerComponent } from '../../common/calendar-picker/calendar-picker.component';
import { PreDefinedSlotListComponent } from './pre-defined-slot-list/pre-defined-slot-list.component';
import { PreDefinedSlotServicesComponent } from './pre-defined-slot-services/pre-defined-slot-services.component';
import { getDomRoot } from '../../../services/dom-root.util';
import {
  adyenErrorMessageKey,
  isAlreadyBookedWithPayment,
  isBookingOutcomeUnknown,
  isPaidBookingStillCompleting,
  isPaymentSessionSpent,
  REFUND_NOTICE_KEY,
  refundNoticeKeyFor,
} from '../../../services/booking-outcome.util';
import { isInvoiceAddressMandatoryFor } from '../../../services/invoice-address.util';
import { calculateIncludedVat, calculateVat, isVatAddedOnTop, TaxableLine, VatResult, vatTypeLabelKey } from '../../../services/vat-calculation.util';

const components = [
  AlertComponent, NewAlertComponent, ButtonComponent, LoaderComponent, FinalpageComponent,
  PersonalDataFormComponent, CwPaymentMethodsComponent, CwCardHeaderComponent, CwNewCardComponent, CwTitleComponent,
  NgxClampComponent, CalendarPickerComponent, PreDefinedSlotListComponent, PreDefinedSlotServicesComponent
];
const modules = [FormsModule, TranslateModule, FontAwesomeModule];
const pipes = [DatePipe, TitleCasePipe, TranslationPipe, CalioCurrencyPipe, TrustHtmlPipe];

/** Booking flow steps for pre-defined slots */
enum PreDefinedSlotStep {
  SERVICE_SELECTION = 1,
  SLOT_SELECTION = 2,
  PERSONAL_FORM = 3,
  SUMMARY = 4,
  SUCCESS = 5,
  FAILURE = 6
}

@Component({
  selector: 'app-pre-defined-slots',
  templateUrl: './pre-defined-slots.component.html',
  styleUrl: './pre-defined-slots.component.scss',
  imports: [NgClass, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownItem, ...components, ...modules, ...pipes]
})
export class PreDefinedSlotsComponent implements OnInit, OnChanges {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;

  @Input() partner: Partner;
  @Input() globals: GlobalObjects;
  @Input() lang: string;
  @Input() widgetColorConf: WidgetColorConf;
  @Input() widgetConf: WidgetConf;
  @Input() selectedJsonCustomerDetail: CustomerPrefillDataModel;
  @Input() selectedJsonCustomFields: { [key: number]: { type: string, value: any } | any };
  @Input() langSwitcher = true;
  @Input() utmSource: string;
  @Input() utmMedium: string;
  @Input() utmCampaign: string;
  @Input() utmContent: string;
  @Input() utmTerm: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() customer: any;
  @Input() selectedAppointmentServiceIds: string | string[];
  @Input() customer_notification_preference: string;
  @Input() internalComment: string;
  @Input() bookerWorkerId: number;
  @Input() redirectedPaymentIntent: string;
  @Input() redirectedPaymentIntentClientSecret: string;
  @Input() pdsStripeRedirectionData: Record<string, any>;
  /** An Adyen payment this tab parked before leaving the page, now back (see AdyenResumeService). */
  @Input() adyenResume?: AdyenResumeReturn;
  @Input() preApplyCoupon: string;

  @Output() bookingSuccessEvent = new EventEmitter<void>();

  @ViewChild('preDefinedSlotForm') preDefinedSlotForm: NgForm;

  private customEventService = inject(CustomEventService);
  private dateUtilsService = inject(DateUtilsService);
  private destroyRef = inject(DestroyRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private formsService = inject(FormsService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);
  private partnerService = inject(PartnerService);
  private paymentService = inject(PaymentService);
  /** Book waits while the payment form is down behind a "check your email first" notice. */
  protected readonly payAgainRequired = this.paymentService.payAgainRequired;
  private preDefinedSlotService = inject(PreDefinedSlotService);
  private utilService = inject(UtilService);
  private timezoneService = inject(TimezoneService);
  private widgetUtilService = inject(WidgetUtilService);
  private translate = inject(TranslateService);
  private adyenResumeService = inject(AdyenResumeService);
  /** What cw-adyen parks before an Adyen payment leaves the page: the Stripe redirect snapshot. */
  protected readonly adyenResumeSnapshot = (): AdyenResumeSnapshot => ({
    host: 'pds',
    data: this.buildRedirectionSnapshot(),
  });

  protected get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.date;
  }

  // When partner has opted into 'partner' mode, booking payloads carry the partner's
  // configured timezone regardless of visitor's browser TZ. Falls back to visitor TZ otherwise.
  private get effectiveDisplayTimezone(): string {
    if (this.partner?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER && this.partner?.timezone) {
      return this.partner.timezone;
    }
    return this.userState?.timezone ?? this.timezoneService.localTimezone;
  }

  protected get appointmentCartForMeetingTypes(): CartItem[] {
    if (!this.selectedService?.id) return [];
    return [{ servicesIds: [this.selectedService.id], total: { price: this.selectedService.price || 0 } } as CartItem];
  }

  protected readonly steps = PreDefinedSlotStep;
  protected readonly widgetConstants = WIDGET_CONSTANTS;
  protected readonly appointmentConstant = APPOINTMENT_CONSTANT;
  protected readonly templateContent = TRANSLATION_TEMPLATES;
  protected readonly deployUrl = environment.deployUrl;

  protected currentStep = PreDefinedSlotStep.SERVICE_SELECTION;
  protected serviceCategories: PreDefinedSlotServiceCategoryGroup[] = [];
  protected selectedService: PreDefinedSlotServiceDao;
  protected servicesLoading = false;
  protected slots: PreDefinedSlotDao[] = [];
  protected selectedSlot: PreDefinedSlotDao;
  protected loading = false;
  protected bookingInProgress = false;
  protected bookingError = '';
  protected userState: UserState = {} as UserState;
  protected showSuccess = false;
  protected showFailure = false;
  protected widgetServiceLabelTemplate: CustomBookingMessageTemplate;
  protected widgetSlotsLabelTemplate: CustomBookingMessageTemplate;
  protected widgetPersonalDetailsLabelTemplate: CustomBookingMessageTemplate;
  protected widgetSummaryLabelTemplate: CustomBookingMessageTemplate;
  protected bookButtonTemplate: CustomBookingMessageTemplate;
  protected previousButtonTemplate: CustomBookingMessageTemplate;
  protected nextButtonTemplate: CustomBookingMessageTemplate;
  protected cartTitleTemplate: CustomBookingMessageTemplate;
  protected cartSubTitleTemplate: CustomBookingMessageTemplate;
  protected successTitleTemplate: CustomBookingMessageTemplate;
  protected manageAppointmentNowTemplate: CustomBookingMessageTemplate;
  protected downloadBookingDetailTemplate: CustomBookingMessageTemplate;
  protected selectDayInCalendarMsgTemplate: CustomBookingMessageTemplate;
  protected successFullBookings: { landingPageLink: string; bookingUUID: string }[] = [];
  protected successMessage: string;
  protected appointmentCustomFields: CustomFieldsDao[] = [];
  protected customFieldValues: { [key: number]: any } = {};
  private allCustomFields: CustomFieldsDao[] = [];
  private readonly bookingCustomFieldTypes = BOOKING_CUSTOM_FIELD_TYPES;

  // Payment state
  protected readonly paymentTypes = PAYMENT_TYPES;
  protected disableBookingButton = false;
  protected stripeElementLoading = false;
  protected totalCartPrice = 0;
  protected isStripeEnabled = false;
  protected isPaypalEnabled = false;
  protected isSaferpayEnabled = false;
  protected isAdyenEnabled = false;
  protected isOnlinePaymentMandatory = false;
  protected isServiceOnlinePaymentEnabled = 0;
  protected isBexioEnabled = false;
  protected isExternalInvoiceEnabled = false;
  protected isStoreEnabled = false;
  protected isLexOfficePaymentTypeEnabled = false;
  protected invoiceAddressChecked = false;
  protected invoiceAddressValid = true;
  protected invoiceAddress: AppointmentBookItem['invoice_address'] = undefined;
  protected paymentType: string;
  protected paypalSetting: PayPalSettingsDbModel;
  protected stripeConnectedAccountId: string;
  protected defaultPaymentType = '';
  protected storageComplete = false;
  protected coupon: CouponDbModel;
  protected finalCouponDiscount = 0;
  /** Gross for a `plus` partner: what cw-payment-methods charges. */
  protected priceAfterCouponReduction: number;
  /** VAT on top of the discounted net (`plus` partners only). */
  protected readonly cartVat = signal<VatResult>({ byRate: [], totalCents: 0 });
  private stripePaymentMethodId: string;
  private paypalOrderDetail: PayPalOrderDetailModel;
  private saferPayPaymentDetails: SaferPayPaymentDetailsDbModel;
  protected adyenPaymentDetails: AdyenPaymentDetails;
  /**
   * An Adyen payment is running: the hosted window is open, or Drop-in is authorising. Back, the
   * step bar, the cart, the personal data and Book stay locked until it reports back; the payment's
   * own "Cancel payment" is the way out, and it releases this through onAdyenReset().
   */
  protected readonly adyenPaymentInFlight = signal(false);
  protected isPaymentInProgress = false;
  protected showRefundNotice = false;
  protected refundNoticeKey = REFUND_NOTICE_KEY;
  protected refundTransactionId: string;
  protected showFullscreenOverlay = false;
  protected bookingErrorMessage: string;
  protected showStripeRedirectionLoader = false;
  protected featureUnavailable = false;
  protected slotViewMode: 'list' | 'calendar' = 'list';
  protected selectedCalendarDate: string;
  protected calendarSlotsForDate: PreDefinedSlotDao[] = [];

  // Calendar picker state (reuses appointment widget's CalendarPickerComponent)
  protected calendarViewDate = new Date();
  protected calendarDays = new Array<CalendarMonthViewDay>();
  protected calendarSlotsCountList: { date: Date; slotsCount: number }[] = [];
  protected calendarDisablePrev = true;
  protected calendarLoaded = true;
  private slotDateMap: { [date: string]: PreDefinedSlotDao[] } = {};
  private previouslySelectedCalendarDay: CalendarMonthViewDay;

  ngOnInit(): void {
    if (!this.partnerService.hasFeature('is_resource_planning_enabled')) {
      this.featureUnavailable = true;
      return;
    }

    this.subscribeToCustomEvents();
    this.listenFromParentIFrame();

    if (this.redirectedPaymentIntent && this.redirectedPaymentIntentClientSecret && this.pdsStripeRedirectionData) {
      this.handleStripeRedirectRecovery();
    } else if (this.adyenResume?.record.snapshot.data?.['selectedSlot']) {
      this.resumeAdyenPayment(this.adyenResume);
    } else {
      this.loadServices();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.widgetTemplates?.currentValue) {
      this.widgetTemplates?.length && this.setupTemplates();
    }
  }

  private subscribeToCustomEvents(): void {
    this.customEventService.childCustomFieldsLoadedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(childFields => {
        this.allCustomFields.push(...childFields);
        this.appointmentCustomFields = this.allCustomFields.filter(cf => !cf.is_addon);
      });

    this.customEventService.removeChildCustomFieldsEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(idsToRemove => {
        this.allCustomFields = this.allCustomFields.filter(cf => !idsToRemove.includes(cf.id));
        this.appointmentCustomFields = this.allCustomFields.filter(cf => !cf.is_addon);
      });
  }

  private listenFromParentIFrame(): void {
    window.addEventListener('message', (event: MessageEvent) => {
      if (document.referrer) {
        const referrer = new URL(document.referrer);
        if (
          [`${referrer.protocol}//${referrer.hostname}`, referrer.origin].includes(event.origin)
        ) {
          if (
            event.data?.action &&
            event.data?.eventName &&
            event.data.eventName === PARENT_TO_CHILD_IFRAME_EVENTS.NAVIGATION
          ) {
            this.handleParentToChildNavigation(event);
          }
        }
      }
    });
  }

  private handleParentToChildNavigation(event: MessageEvent): void {
    if (!Object.values(PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS).includes(event.data.action)) return;

    switch (this.currentStep) {
      case PreDefinedSlotStep.SERVICE_SELECTION:
        if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.NEXT) {
          if (!this.selectedService) {
            this.widgetUtilService.sendMessageToParent({
              eventName: PARENT_TO_CHILD_IFRAME_EVENTS.VALIDATION,
              controls: [PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME.SERVICE],
            });
          }
        }
        break;
      case PreDefinedSlotStep.SLOT_SELECTION:
        if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.PREVIOUS) {
          this.currentStep = PreDefinedSlotStep.SERVICE_SELECTION;
        } else if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.NEXT) {
          if (!this.selectedSlot) {
            this.widgetUtilService.sendMessageToParent({
              eventName: PARENT_TO_CHILD_IFRAME_EVENTS.VALIDATION,
              controls: [PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME.SLOT],
            });
          }
        }
        break;
      case PreDefinedSlotStep.PERSONAL_FORM:
        if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.PREVIOUS) {
          this.currentStep = PreDefinedSlotStep.SLOT_SELECTION;
        } else if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.BOOK) {
          this.submitBooking();
        }
        break;
      case PreDefinedSlotStep.SUMMARY:
        if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.PREVIOUS) {
          this.currentStep = PreDefinedSlotStep.PERSONAL_FORM;
        } else if (event.data.action === PARENT_TO_CHILD_IFRAME_EVENT_ACTIONS.BOOK) {
          this.submitBooking();
        }
        break;
    }
  }

  private handleStripeRedirectRecovery(): void {
    LoggerService.log('[PreDefinedSlots] Handling Stripe redirect recovery');

    const data = this.pdsStripeRedirectionData;
    this.paymentType = this.paymentTypes.CREDITCARD;
    this.stripePaymentMethodId = this.redirectedPaymentIntent;
    this.bookingInProgress = true;
    this.bookingErrorMessage = undefined;
    this.showStripeRedirectionLoader = true;

    // Restore state from localStorage
    this.restoreRedirectionSnapshot(data);

    this.currentStep = PreDefinedSlotStep.PERSONAL_FORM;
    this.doBooking();
  }

  private setupTemplates(): void {
    this.widgetServiceLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NAV_SERVICE_LABEL
    );

    this.widgetSlotsLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.EVENT_WIDGET_LABELS.WIDGET_EVENT_NAV_SLOTS_LABEL
    );

    this.widgetPersonalDetailsLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NAV_PERSONAL_DETAILS_LABEL
    );

    this.widgetSummaryLabelTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NAV_SUMMARY_LABEL
    );

    this.bookButtonTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_BOOK_BUTTON
    );

    this.previousButtonTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_PREVIOUS
    );

    this.nextButtonTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_NEXT
    );

    this.cartTitleTemplate = this.widgetTemplates.find(t => t.id === 124);
    this.cartTitleTemplate && (this.cartTitleTemplate.is_multi_language = 1);

    this.cartSubTitleTemplate = this.widgetTemplates.find(t => t.id === 125);
    this.cartSubTitleTemplate && (this.cartSubTitleTemplate.is_multi_language = 1);

    this.successTitleTemplate = this.widgetTemplates.find(
      t => t.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_SUCCESS_TITLE_LABEL
    );
    this.successTitleTemplate && (this.successTitleTemplate.is_multi_language = 1);

    this.manageAppointmentNowTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_MANAGE_BOOKING_LABEL
    );

    this.downloadBookingDetailTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_DOWNLOAD_APPOINTMENT_ICS_FILE
    );

    this.selectDayInCalendarMsgTemplate = this.helperService.findTemplate(
      this.widgetTemplates, WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_CHOOSE_DATE_HINT
    );
  }

  private loadServices(): void {
    this.servicesLoading = true;
    this.preDefinedSlotService.getAvailableServices()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.servicesLoading = false)
      )
      .subscribe({
        next: (categories) => {
          this.serviceCategories = categories;
          LoggerService.log('[PreDefinedSlots] Loaded service categories:', categories.length);
          this.tryAutoSelectService();
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.error('[PreDefinedSlots] Error loading services:', error);
          this.serviceCategories = [];
        }
      });
  }

  private loadSlots(): void {
    this.loading = true;
    this.preDefinedSlotService.getAvailableSlots(this.selectedService?.uuid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (slots) => {
          this.slots = slots;
          this.buildCalendarData();
          LoggerService.log('[PreDefinedSlots] Loaded slots:', slots.length);
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.error('[PreDefinedSlots] Error loading slots:', error);
          this.slots = [];
        }
      });
  }

  protected onServiceSelected(service: PreDefinedSlotServiceDao): void {
    this.selectedService = service;
  }

  protected onNextFromServiceSelection(): void {
    if (!this.selectedService) {
      return;
    }
    this.currentStep = PreDefinedSlotStep.SLOT_SELECTION;
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_START);
    this.loadSlots();
    this.loadCustomFields();
  }

  protected onPreviousFromSlotSelection(): void {
    this.selectedSlot = undefined;
    this.currentStep = PreDefinedSlotStep.SERVICE_SELECTION;
  }

  /** Build calendarSlotsCountList + slotDateMap from loaded slots (for CalendarPickerComponent) */
  private buildCalendarData(): void {
    const timezone = this.userState?.timezone || this.timezoneService.getLocalTimezone();
    this.slotDateMap = {};

    this.slots.forEach(slot => {
      const date = moment.utc(slot.start_utc).tz(timezone).format(DATE_ISO_FORMAT);
      if (!this.slotDateMap[date]) {
        this.slotDateMap[date] = [];
      }
      this.slotDateMap[date].push(slot);
    });

    // Start with empty list — badges only shown when user clicks a date
    this.calendarSlotsCountList = [];
    this.calendarLoaded = true;
  }

  protected onCalendarDayClicked(event: { day: CalendarMonthViewDay }): void {
    const day = event.day;
    if (!day?.inMonth) return;

    const dateStr = moment(day.date).format(DATE_ISO_FORMAT);
    if (this.slotDateMap[dateStr]?.length) {
      // Clear previous day selection
      if (this.previouslySelectedCalendarDay) {
        this.previouslySelectedCalendarDay.cssClass = '';
        this.previouslySelectedCalendarDay.badgeTotal = 0;
      }

      // Mark clicked day as selected (circle, no badge yet)
      day.cssClass = 'cal-selected';
      this.previouslySelectedCalendarDay = day;

      this.selectedCalendarDate = dateStr;
      this.calendarSlotsForDate = this.slotDateMap[dateStr];

      // Reset slot selection when changing date
      this.selectedSlot = undefined;
      this.calendarSlotsCountList = [];
    }
  }

  protected onCalendarViewDateChanged(event: { viewDate: Date; isNext: boolean }): void {
    this.calendarViewDate = event.viewDate;
    this.calendarDisablePrev = moment(event.viewDate).isSameOrBefore(moment(), 'month');
  }

  protected onCalendarMarkExceptionDays(event: { body: CalendarMonthViewDay[] }): void {
    this.calendarDays = event.body;

    // Mark in-month future days without slots as unavailable (faded)
    for (const day of this.calendarDays) {
      if (day.inMonth && !day.isPast) {
        const dateStr = moment(day.date).format(DATE_ISO_FORMAT);
        if (!this.slotDateMap[dateStr]?.length) {
          day.cssClass = (day.cssClass || '') + ' pds-unavailable';
        }
      }
    }
  }

  protected onSlotSelected(slot: PreDefinedSlotDao): void {
    if (this.selectedSlot?.uuid === slot.uuid) {
      // Deselect → remove badge, keep circle
      this.selectedSlot = undefined;
      this.calendarSlotsCountList = [];
      if (this.previouslySelectedCalendarDay) {
        this.previouslySelectedCalendarDay.badgeTotal = 0;
      }
    } else {
      // Select → show badge on selected date
      this.selectedSlot = slot;
      if (this.previouslySelectedCalendarDay) {
        this.previouslySelectedCalendarDay.badgeTotal = 1;
        this.calendarSlotsCountList = [{
          date: this.previouslySelectedCalendarDay.date,
          slotsCount: 1,
        }];
      }
    }
  }

  protected goToStep(step: PreDefinedSlotStep): void {
    if (this.adyenPaymentInFlight()) {
      // Leaving the payment step would tear down the payment the shopper may be completing now.
      return;
    }
    this.currentStep = step;
    this.scrollToComponent();
  }

  protected onNextFromSlotSelection(): void {
    if (!this.selectedSlot) {
      return;
    }
    this.totalCartPrice = this.selectedService?.price || 0;
    this.applyNetAfterCoupon(this.totalCartPrice);
    this.verifyOnlinePaymentStatus();
    this.currentStep = PreDefinedSlotStep.PERSONAL_FORM;
    this.scrollToComponent();
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_SELECT_SLOT);
  }

  protected onPreviousFromPersonalForm(): void {
    if (this.adyenPaymentInFlight()) {
      return;
    }
    this.resetPaymentAndCouponState();
    this.currentStep = PreDefinedSlotStep.SLOT_SELECTION;
    this.scrollToComponent();
  }

  protected get isSplitMode(): boolean {
    return Number(this.partner?.split_personal_form_and_summary_in_widget) === 1;
  }

  protected onNextFromPersonalForm(): void {
    this.currentStep = PreDefinedSlotStep.SUMMARY;
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_PERSONAL_INFO);
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_SUMMARY);
  }

  protected onPreviousFromSummary(): void {
    if (this.adyenPaymentInFlight()) {
      return;
    }
    this.resetPaymentAndCouponState();
    this.currentStep = PreDefinedSlotStep.PERSONAL_FORM;
  }

  protected removeFromCart(): void {
    if (this.adyenPaymentInFlight()) {
      // The payment running now was minted for this slot.
      return;
    }
    this.selectedSlot = undefined;
    this.currentStep = PreDefinedSlotStep.SLOT_SELECTION;
    this.customEventService.cartItemsChangeEvent.emit({ cart: [], type: WIDGET_CONSTANTS.APPOINTMENT });
  }

  /**
   * Adyen authorised - the one entry point for every Adyen result, so one result can start at most
   * one booking. No Book press drives the payment here: Drop-in has its own pay button and the
   * hosted page its own, so the shopper has been charged with nothing booked. Book now, exactly as a
   * press would, unless something would stop that press: then the payment stays shown as done and
   * the shopper completes the rest and presses Book.
   */
  protected onAdyenPaid(details: AdyenPaymentDetails): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = details;
    if (this.canBookOnItsOwn()) {
      this.submitBooking();
    }
  }

  /** Drop-in or the hosted window really started a payment: lock the booking until it reports back. */
  protected onAdyenPaymentStarted(): void {
    this.adyenPaymentInFlight.set(true);
  }

  /** Refused, cancelled, closed without paying, or an SDK error: the booking unlocks. */
  protected onAdyenReset(): void {
    this.adyenPaymentInFlight.set(false);
    this.adyenPaymentDetails = undefined;
  }

  /**
   * Would a Book press go through now? A booking already on its way disables Book, so a result
   * reported twice cannot send a second /book.
   */
  private canBookOnItsOwn(): boolean {
    return canBookAfterAdyenPayment({
      paymentHeld: this.paymentType === this.paymentTypes.ADYEN && !!this.adyenPaymentDetails,
      bookingBlocked: this.bookingInProgress
        || this.disableBookingButton
        || this.isInvoiceAddressFormBlockingBooking
        || this.payAgainRequired(),
      // Pre-defined slots keep no slot reservation.
      reservationExpired: false,
      formIncomplete: !this.selectedSlot
        || this.formsService.imageUploaded === false
        || !!this.preDefinedSlotForm?.invalid,
      // A Book press here does not gate on the captcha either; the booking request carries it.
      captchaPending: false,
    });
  }

  protected submitBooking(): void {
    // Book is locked while an Adyen payment runs; a successful payment books by itself.
    if (!this.selectedSlot || this.bookingInProgress || this.adyenPaymentInFlight()) {
      return;
    }

    if (this.formsService.imageUploaded === false) {
      LoggerService.warn('[PreDefinedSlots] Image is uploading...');
      return;
    }

    if (this.preDefinedSlotForm?.invalid) {
      LoggerService.warn('[PreDefinedSlots] Form validation failed');
      this.markFormGroupTouched();
      this.isPaymentBookButtonValidated();
      this.widgetUtilService.sendMessageToParent({
        eventName: PARENT_TO_CHILD_IFRAME_EVENTS.VALIDATION,
        controls: [PARENT_TO_CHILD_IFRAME_VALIDATION_CONTROL_NAME.PERSONAL_FORM],
      });
      return;
    }

    // When a 100% coupon reduces the price to 0, switch to STORE payment
    if (this.totalCartPrice <= 0 || this.priceAfterCouponReduction <= 0) {
      this.paymentType = this.paymentTypes.STORE;
    }

    // Route to payment-specific flows that validate form → trigger SDK → callback → doBooking()
    if (this.paymentType === this.paymentTypes.SAFERPAY) {
      this.showFullscreenOverlay = true;
      this.paymentService.initiateSaferpaySubject.next(true);
    } else if (this.paymentType === this.paymentTypes.CREDITCARD) {
      if (this.stripePaymentMethodId) {
        this.doBooking();
        return;
      }
      this.bookingInProgress = true;
      this.paymentService.initiateStripeSubject.next(true);
    } else {
      this.doBooking();
    }
  }

  private doBooking(): void {
    if (this.isPaymentRequired() && !this.isPaymentBookButtonValidated()) {
      return;
    }

    this.bookingInProgress = true;
    this.bookingError = '';
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING);

    const customerData = this.buildCustomerData();

    this.adyenResumeService.markBookingSent(this.partner?.id, this.adyenPaymentDetails?.session_id);
    this.preDefinedSlotService.bookSlot(this.selectedSlot.uuid, customerData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.bookingInProgress = false)
      )
      .subscribe({
        next: (response) => {
          this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          LoggerService.log('[PreDefinedSlots] Booking successful:', response);
          this.showStripeRedirectionLoader = false;

          if (response?.data?.uuid) {
            this.successFullBookings = [{
              landingPageLink: environment.calioDashboardBaseUrl + '/appointments/' + response.data.uuid,
              bookingUUID: response.data.uuid,
            }];
          }

          this.createSuccessMessage();
          this.currentStep = PreDefinedSlotStep.SUCCESS;
          this.showSuccess = true;

          // Analytics: emit booking success to parent window, GTM, Adobe Launch
          this.googleAnalyticsService.emitEventToParent(WINDOW_LISTENER_CONSTANTS.PRE_DEFINED_SLOT_BOOKING_DONE, response?.data);
          this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_SUCCESS);
          this.googleAnalyticsService.generateSessionUuid();

          this.bookingSuccessEvent.emit();

          // Post-booking redirect if configured
          if (
            this.partnerService.hasFeature('is_external_success_page_forwarding_enabled') &&
            this.partner.is_redirect_after_booking_enabled &&
            this.partner.successful_booking_redirect_url &&
            this.partner.redirect_after_booking_action === BOOKING_REDIRECTION_VIEWS.BOOKING_SUMMARY_VIEW &&
            !this.globals.isInternal
          ) {
            this.helperService.redirectToUrl(this.partner.successful_booking_redirect_url);
          }
        },
        error: (error: HttpErrorResponse) => {
          // A paid booking still held by the booking lock after every resend is most likely being
          // completed: keep this tab's record that /book was sent, so a reload cannot start a
          // second payment beside it.
          const stillCompleting = isPaidBookingStillCompleting(error, !!this.adyenPaymentDetails);
          if (!stillCompleting) {
            this.adyenResumeService.releaseIfBookingSent(this.partner?.id);
          }
          LoggerService.error('[PreDefinedSlots] Booking failed:', error);
          this.isPaymentInProgress = false;
          this.showFullscreenOverlay = false;
          this.bookingInProgress = false;

          this.showRefundNotice = !!(this.stripePaymentMethodId || this.paypalOrderDetail || this.saferPayPaymentDetails || this.adyenPaymentDetails);
          // A lost answer is not a refusal: the booking may exist, so no refund is promised.
          this.refundNoticeKey = refundNoticeKeyFor(error, !!this.adyenPaymentDetails);
          this.refundTransactionId = this.stripePaymentMethodId
            || this.paypalOrderDetail?.paypal_order_id
            || this.saferPayPaymentDetails?.transaction_id
            || this.adyenPaymentDetails?.session_id;
          // After /book fails the payment tokens are dead — Stripe PI is already
          // captured (refunded async by BE cron), PayPal order is consumed, Saferpay
          // auth will expire. Invalidate locally and ask the Stripe component to
          // create a fresh PI so the next click can't reuse a dead token.
          const stripeWasUsed = !!this.stripePaymentMethodId;
          const paypalOrSaferpayWasUsed = !!(this.paypalOrderDetail || this.saferPayPaymentDetails || this.adyenPaymentDetails);
          // A paid booking whose answer was lost (connection dropped, 504) may have gone through.
          const outcomeUnknown = paypalOrSaferpayWasUsed && isBookingOutcomeUnknown(error);
          // The backend will not claim this payment session again - which does not prove a booking.
          const sessionSpent = isPaymentSessionSpent(error);
          // The backend says this payment already backs a confirmed booking: that booking exists.
          const alreadyBooked = isAlreadyBookedWithPayment(error);
          this.stripePaymentMethodId = undefined;
          this.paypalOrderDetail = undefined;
          this.saferPayPaymentDetails = undefined;
          this.adyenPaymentDetails = undefined;
          // Reset so the next storeBookingItemsEvent goes false -> true and the
          // Stripe component's @Input setter actually fires handlePayment().
          // Without this, the second Book click updates the PI but never calls
          // confirmPayment() and the loader hangs.
          this.storageComplete = false;
          if (stripeWasUsed) {
            this.paymentService.recreatePaymentIntentSubject.next();
          }
          if (stillCompleting) {
            // Check the email, and NO "Pay again": the booking is most likely going through.
            this.paymentService.bookingStillCompletingSubject.next();
            this.bookingErrorMessage = undefined;
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_FAILED);
            return;
          }
          if (alreadyBooked) {
            // The booking exists: its notice, the payment form kept down and NO "Pay again".
            this.paymentService.paymentAlreadyBookedSubject.next();
            this.bookingErrorMessage = undefined;
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_FAILED);
            return;
          }
          if (outcomeUnknown || sessionSpent) {
            // The booking may exist, so the payment form must NOT come back on its own: that invited
            // a second payment beside a "check your email" notice. The customer re-arms it with
            // "Pay again" once they have checked - and no red "booking failed" stands beside it.
            // A spent session is handled the same way, in its own words.
            if (sessionSpent) {
              this.paymentService.paymentSessionSpentSubject.next();
            } else {
              this.paymentService.bookingOutcomeUnknownSubject.next();
            }
            this.bookingErrorMessage = undefined;
            this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_FAILED);
            return;
          }
          if (paypalOrSaferpayWasUsed) {
            // Brings the PayPal button / Saferpay launcher / Adyen Drop-in back into the DOM
            // so the customer can authorize a fresh payment for the retry.
            this.paymentService.resetPaymentSuccessStateSubject.next();
          }

          if (this.showStripeRedirectionLoader) {
            this.showStripeRedirectionLoader = false;
            this.bookingErrorMessage = error?.error?.errors?.[0]?.message || error?.message;
            this.stripePaymentMethodId = null;
            this.storageComplete = false;
            this.verifyOnlinePaymentStatus();

            setTimeout(() => {
              const root = getDomRoot(this.elementRef.nativeElement);
              const element = root.getElementById('pds-booking-error-message') || root.getElementById('pds-summary-booking-error-message');
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 1000);
            return;
          }

          // Two things are load-bearing here.
          //
          // The 500 test comes FIRST because the API's error renderer replaces the body of
          // every non-HttpException with Cake's own 'An Internal Error Has Occurred.' - so
          // reading errors[0].message ahead of it printed developer text to the customer and
          // left this branch unreachable.
          //
          // And the keys are translated at the assignment, not in the template: the binding
          // renders this value as innerHtml with no translate pipe, so the customer was
          // literally reading the string 'common.noDateError'. It is not moved to the template
          // because the bookingErrors branch assigns an array, which the pipe would mangle.
          if (error?.status === HttpStatusCode.InternalServerError) {
            // After a payment a 500 is not evidence of a taken slot (a refused payment check answers
            // 500 too), so the paid case gets the generic failure beside the refund notice.
            this.bookingErrorMessage = this.translate.instant(
              this.showRefundNotice ? 'pre_defined_slots.booking_failed' : 'common.noDateError',
            );
          } else if (adyenErrorMessageKey(error)) {
            // A keyed Adyen refusal, in the customer's language.
            this.bookingErrorMessage = this.translate.instant(adyenErrorMessageKey(error));
          } else if (error?.error?.errors?.[0]?.message) {
            this.bookingErrorMessage = error.error.errors[0].message;
          } else if (error?.error?.bookingErrors?.[0]?.message) {
            this.bookingErrorMessage = error.error.bookingErrors.map(e => e.message);
          } else if (error?.status === HttpStatusCode.Conflict) {
            this.bookingErrorMessage = this.translate.instant('pre_defined_slots.slot_already_booked');
          } else {
            this.bookingErrorMessage = this.translate.instant('pre_defined_slots.booking_failed');
          }

          this.storageComplete = false;

          // Analytics: emit booking failure
          this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.PRE_DEFINED_SLOT_BOOKING_STEP_FAILED);
        }
      });
  }

  protected onBookAnotherAppointment(): void {
    this.showSuccess = false;
    this.selectedSlot = undefined;
    this.selectedService = undefined;
    this.successFullBookings = [];
    this.resetPaymentAndCouponState();
    this.currentStep = PreDefinedSlotStep.SERVICE_SELECTION;
    this.loadServices();
  }

  protected onRetryBooking(): void {
    this.bookingError = '';
    this.showFailure = false;
    this.selectedSlot = undefined;
    this.selectedService = undefined;
    this.resetPaymentAndCouponState();
    this.currentStep = PreDefinedSlotStep.SERVICE_SELECTION;
    this.loadServices();
  }

  private buildCustomerData(): Record<string, any> {
    return {
      title: this.userState?.title,
      prename: this.userState?.forename,
      lastname: this.userState?.lastName,
      email: this.userState?.eMail,
      phone: this.userState?.phone,
      mobile: this.userState?.mobile,
      gender: this.userState?.gender,
      comment: this.userState?.comment,
      company_name: (this.userState?.company || undefined),
      street: this.userState?.street,
      zip: this.userState?.zip,
      city: this.userState?.city,
      country: this.userState?.country_id,
      birthday: this.userState?.birthday,
      customer_id: this.userState?.customerId,
      customer_uuid: this.userState?.customerUuid,
      customer_notification_preference: this.userState?.customer_notification_preference || this.customer_notification_preference,
      internal_comment: (this.userState?.internal_comment || this.internalComment || undefined),
      ...(this.globals?.isInternal && this.bookerWorkerId && { booker_worker_id: this.bookerWorkerId }),
      appointment_source_id: this.globals?.isInternal ? APPOINTMENT_SOURCE_ID.INTERNAL_WIDGET : APPOINTMENT_SOURCE_ID.EXTERNAL_WIDGET,
      is_internal: this.globals?.isInternal ? true : false,
      ...(this.userState?.acceptDataPolicy !== undefined && { accept_data_policy: +this.userState.acceptDataPolicy }),
      ...(this.userState?.meeting_type_id && { meeting_type_id: this.userState.meeting_type_id }),
      ...(this.paymentType && { payment_type: this.paymentType }),
      ...(this.paymentType === this.paymentTypes.EXTERNAL_INVOICE && this.invoiceAddressChecked && this.invoiceAddress
        ? { invoice_address: this.invoiceAddress }
        : {}),
      ...(this.stripePaymentMethodId && { stripe_payment_method_id: this.stripePaymentMethodId }),
      ...(this.stripePaymentMethodId && { stripe_payment_intent_id: this.stripePaymentMethodId }),
      ...(this.paypalOrderDetail && {
        paypal_order_id: this.paypalOrderDetail.paypal_order_id,
        paypal_payer_id: this.paypalOrderDetail.paypal_payer_id,
        paypal_status: this.paypalOrderDetail.paypal_status,
        paypal_raw_data: this.paypalOrderDetail.paypal_raw_data,
        paypal_capture_id: this.paypalOrderDetail.paypal_capture_id,
        paypal_payment_log_id: this.paypalOrderDetail.paypal_payment_log_id,
      }),
      ...(this.adyenPaymentDetails && {
        // Opaque handles only — ResourcePlanningService re-reads the session
        // from Adyen and trusts that, not anything the browser asserts.
        adyen_session_id: this.adyenPaymentDetails.session_id,
        adyen_session_result: this.adyenPaymentDetails.session_result,
      }),
      ...(this.saferPayPaymentDetails && {
        saferpay_transaction_id: this.saferPayPaymentDetails.transaction_id,
        saferpay_payment_method: this.saferPayPaymentDetails.payment_method,
      }),
      ...(this.coupon && { coupon_code: this.coupon.code }),
      amount: this.totalCartPrice,
      captcha: (this.userState?.captcha_challenge || undefined),
      friendly_captcha: (this.userState?.friendly_captcha_challenge || undefined),
      ...this.partner?.is_timezone_enabled && { timezone: this.effectiveDisplayTimezone },
      customFieldValues: this.buildCustomFieldValues(),
      utm_source: ((this.utmSource && this.utmSource !== 'null') ? this.utmSource : undefined),
      utm_medium: ((this.utmMedium && this.utmMedium !== 'null') ? this.utmMedium : undefined),
      utm_campaign: ((this.utmCampaign && this.utmCampaign !== 'null') ? this.utmCampaign : undefined),
      utm_content: ((this.utmContent && this.utmContent !== 'null') ? this.utmContent : undefined),
      utm_term: ((this.utmTerm && this.utmTerm !== 'null') ? this.utmTerm : undefined),
      user_browser_language: navigator?.language,
      user_selected_language: this.lang,
    };
  }

  private loadCustomFields(): void {
    if (!this.selectedService?.id) {
      return;
    }
    this.formsService.getAppointmentCustomFields([this.selectedService.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (customFields) => {
          this.allCustomFields = this.initCustomFieldValues(customFields || []);
          this.appointmentCustomFields = this.allCustomFields.filter(cf => !cf.is_addon);
          LoggerService.log('[PreDefinedSlots] Loaded custom fields:', this.appointmentCustomFields.length);
        },
        error: (err: HttpErrorResponse) => {
          LoggerService.error('[PreDefinedSlots] Error loading custom fields:', err);
          this.allCustomFields = [];
          this.appointmentCustomFields = [];
        }
      });
  }

  private initCustomFieldValues(customFields: CustomFieldsDao[]): CustomFieldsDao[] {
    // Translate any identifier-keyed prefill entries to their numeric custom-field id
    // once, before the id-keyed prefill loop below reads selectedJsonCustomFields.
    this.selectedJsonCustomFields = this.helperService.resolveCustomFieldIdentifierKeys(this.selectedJsonCustomFields, customFields);

    const trimmedArray: CustomFieldsDao[] = [];
    const seen: number[] = [];

    for (const cf of customFields) {
      if (seen.includes(cf.id) || cf.parent_custom_field_id) {
        continue;
      }
      trimmedArray.push(cf);
      seen.push(cf.id);

      const selectTypes = [
        this.bookingCustomFieldTypes.SELECT,
        this.bookingCustomFieldTypes.CARD_SELECT,
        this.bookingCustomFieldTypes.IMAGE_SELECT,
      ];

      if (selectTypes.includes(cf.type)) {
        if (!this.customFieldValues[cf.id]) {
          if (Number(cf.is_multiple_select) === 1) {
            const prefill = this.selectedJsonCustomFields?.[cf.id];
            this.customFieldValues[cf.id] = {
              value: prefill ? String(prefill).split(',').map(Number) : [],
              type: cf.type,
            };
          } else {
            const prefill = this.selectedJsonCustomFields?.[cf.id];
            this.customFieldValues[cf.id] = {
              value: prefill ? Number(prefill) : null,
              type: cf.type,
            };
          }
        }
      } else if (cf.type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
        const optionValues = cf.custom_field_options?.map(opt => {
          opt.option_value = opt.number_default;
          return { custom_field_option_id: opt.id, custom_field_option_value: opt.number_default };
        }) || [];
        this.customFieldValues[cf.id] = {
          value: cf.custom_field_options?.map(opt => opt.id) || [],
          option_values: optionValues,
          type: cf.type,
        };
      } else if (cf.type === this.bookingCustomFieldTypes.FILE) {
        this.customFieldValues[cf.id] = {
          type: this.bookingCustomFieldTypes.FILE,
          file_ids: this.customFieldValues[cf.id]?.file_ids?.length > 0 ? this.customFieldValues[cf.id].file_ids : [],
        };
      } else if (cf.type === this.bookingCustomFieldTypes.CHECKBOX) {
        const prefill = this.selectedJsonCustomFields?.[cf.id];
        this.customFieldValues[cf.id] = prefill
          ? ['true', '1', true, 1].includes(prefill)
          : false;
      } else if ([
        this.bookingCustomFieldTypes.TEXT,
        this.bookingCustomFieldTypes.TEXTAREA,
        this.bookingCustomFieldTypes.RADIO,
        this.bookingCustomFieldTypes.DATE,
      ].includes(cf.type)) {
        this.customFieldValues[cf.id] = this.selectedJsonCustomFields?.[cf.id] ?? undefined;
      }
    }

    trimmedArray.sort((a, b) => a.position - b.position);
    return trimmedArray;
  }

  private buildCustomFieldValues(): any[] {
    const fieldIds = Object.keys(this.customFieldValues);
    const rawCustomFieldIds = this.allCustomFields.map(cf => cf.id);
    const validFieldIds = fieldIds.filter(id => rawCustomFieldIds.includes(Number(id)));

    const result: any[] = [];
    for (const fieldId of validFieldIds) {
      const val = this.customFieldValues[fieldId];
      if (val?.type === this.bookingCustomFieldTypes.SELECT) {
        result.push({
          custom_field_id: Number(fieldId),
          type: this.bookingCustomFieldTypes.SELECT,
          value: Array.isArray(val.value) ? val.value : [val.value],
        });
      } else if (val?.type === this.bookingCustomFieldTypes.CARD_SELECT) {
        result.push({ custom_field_id: Number(fieldId), type: this.bookingCustomFieldTypes.CARD_SELECT, value: val.value });
      } else if (val?.type === this.bookingCustomFieldTypes.IMAGE_SELECT) {
        result.push({ custom_field_id: Number(fieldId), type: this.bookingCustomFieldTypes.IMAGE_SELECT, value: val.value });
      } else if (val?.type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
        result.push({ custom_field_id: Number(fieldId), type: this.bookingCustomFieldTypes.NUMBER_SELECT, value: val.value, option_values: val.option_values });
      } else if (val?.type === this.bookingCustomFieldTypes.FILE) {
        result.push({ custom_field_id: Number(fieldId), type: this.bookingCustomFieldTypes.FILE, file_ids: [...(val.file_ids || [])] });
      } else {
        const fieldDef = this.allCustomFields.find(cf => cf.id === Number(fieldId));
        result.push({ custom_field_id: Number(fieldId), type: fieldDef?.type || '', value: val?.toString() });
      }
    }
    return result;
  }

  private createSuccessMessage(): void {
    const bookingInfoTemplate = this.widgetTemplates?.find(
      t => t.identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_BOOKING_INFO_APPOINTMENT
    );
    if (bookingInfoTemplate) {
      bookingInfoTemplate.is_multi_language = 1;
    }
    this.successMessage = this.utilService.getBookingInfoMessage(bookingInfoTemplate, this.lang);

    if (!this.successMessage) {
      const courtesyForm = this.partner?.courtesyForm;
      const key = courtesyForm === 'Du'
        ? 'appointments.finalpage.successMessageDu'
        : 'appointments.finalpage.successMessageSie';
      this.translate.get(key).subscribe(res => {
        this.successMessage = res;
        this.customEventService.finalSuccessMessageChangedEvent.emit(this.successMessage);
      });
    } else {
      this.customEventService.finalSuccessMessageChangedEvent.emit(this.successMessage);
    }
  }

  private verifyOnlinePaymentStatus(): void {
    this.partnerService.verifyOnlinePaymentStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (paymentStatus) => {
          if (!paymentStatus) return;
          this.isServiceOnlinePaymentEnabled = Number(paymentStatus.isServiceOnlinePaymentEnabled);
          if (paymentStatus.isServiceOnlinePaymentEnabled === 1 && paymentStatus.isStripeAppointmentPaymentEnabled === 1 && paymentStatus.stripeEnabled) {
            this.isStripeEnabled = true;
          }
          if (paymentStatus.isOnlinePaymentMandatory === 1) {
            this.isOnlinePaymentMandatory = true;
          }
          if (paymentStatus.isBexioAppointmentServicePaymentEnabled === 1 && paymentStatus.bexioEnabled) {
            this.isBexioEnabled = true;
          }
          if (paymentStatus.isExternalInvoiceAppointmentPaymentEnabled === 1) {
            this.isExternalInvoiceEnabled = true;
            // see appointment.component.ts: the card renders on this flag, so a partner offering
            // only invoice would otherwise never be able to choose a payment type
            this.isServiceOnlinePaymentEnabled = 1;
          }
          if (paymentStatus.isServiceOnlinePaymentEnabled === 1 && paymentStatus.saferpayEnabled) {
            this.isSaferpayEnabled = true;
          }
          if (paymentStatus.isServiceOnlinePaymentEnabled === 1 && paymentStatus.adyenEnabled) {
            this.isAdyenEnabled = true;
          }
          if (paymentStatus.isServiceOnlinePaymentEnabled === 1 && paymentStatus.paypalEnabled) {
            this.isPaypalEnabled = true;
            this.paypalSetting = new PayPalSettingsDbModel();
            this.paypalSetting.access_token = paymentStatus.paypalAccessToken;
            this.paypalSetting.client_id = paymentStatus.paypalClientId;
          }
          if (paymentStatus.onlinePaymentShowOnSitePaymentType === 1) {
            this.isServiceOnlinePaymentEnabled = 1;
            if (paymentStatus.isOnlinePaymentMandatory !== 1) {
              this.isStoreEnabled = true;
            }
          }
          this.isLexOfficePaymentTypeEnabled = (paymentStatus.lexOfficeEnabled === true);
          this.stripeConnectedAccountId = paymentStatus.stripeConnectedAccountId ?? null;
          this.defaultPaymentType = paymentStatus.defaultPaymentType ?? '';
          this.paymentType = undefined;
        }
      });
  }

  protected onChangePaymentType(event: string): void {
    this.paymentType = event;
    this.stripePaymentMethodId = null;
    this.paypalOrderDetail = undefined;
    this.saferPayPaymentDetails = undefined;
    this.adyenPaymentDetails = undefined;
    this.isPaymentInProgress = false;
    this.invoiceAddressChecked = false;
    this.invoiceAddressValid = true;
    this.invoiceAddress = undefined;
    if (event === this.paymentTypes.CREDITCARD && this.isStripeEnabled && (this.priceAfterCouponReduction ?? this.totalCartPrice) > 0) {
      this.disableBookingButton = true;
      this.stripeElementLoading = true;
    } else {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
  }

  protected onStripElementLoadEvent(isStripeLoaded: boolean): void {
    // Loaded or failed, the loading gate must release — a failed load surfaces its
    // error through resetStripeTokenEvent and must not leave the button dead.
    this.stripeElementLoading = false;
    this.disableBookingButton = false;
  }

  protected onStripeSuccessfulEvent(method: string): void {
    this.stripePaymentMethodId = method;
    this.doBooking();
  }

  protected onResetStripeTokenEvent(): void {
    this.stripePaymentMethodId = null;
    this.refundTransactionId = null;
    this.showRefundNotice = false;
    this.bookingInProgress = false;
    this.disableBookingButton = false;
    this.stripeElementLoading = false;
    this.storageComplete = false;
  }

  protected onPaypalSuccessfulEvent(event: PayPalOrderDetailModel): void {
    this.paypalOrderDetail = event;
    this.doBooking();
  }

  protected onResetPaypalEvent(): void {
    this.paypalOrderDetail = null;
    this.isPaymentInProgress = false;
  }

  protected onCancelPayment(): void {
    this.paypalOrderDetail = undefined;
    this.isPaymentInProgress = false;
    this.customEventService.resetPayPalOrderDetailEvent.emit();
  }

  protected onSaferpaySuccessfulEvent(paymentDetails: SaferPayPaymentDetailsDbModel): void {
    this.saferPayPaymentDetails = paymentDetails;
    this.showFullscreenOverlay = false;
    this.doBooking();
  }

  protected onResetSaferpayEvent(errorMessage?: string): void {
    this.bookingErrorMessage = errorMessage || undefined;
    this.saferPayPaymentDetails = undefined;
    this.adyenPaymentDetails = undefined;
    this.showFullscreenOverlay = false;
    this.bookingInProgress = false;
  }

  protected closeSaferpayOverlay(): void {
    this.showFullscreenOverlay = false;
    this.onResetSaferpayEvent('');
    this.paymentService.handleSaferpayBackgroundTriggerManually.next(true);
  }

  protected onCouponSuccessEvent(event: CouponDbModel): void {
    this.coupon = event;
    this.calculateCoupon();
    // A price of 0 unmounts the Stripe element before it can report ready, so the
    // loading gate must be released here or the book button stays disabled forever.
    if (this.priceAfterCouponReduction <= 0) {
      this.disableBookingButton = false;
      this.stripeElementLoading = false;
    }
    this.customEventService.couponChangeEvent.emit(this.finalCouponDiscount);
  }

  /**
   * Standalone "Invoice" payment mode: keeps the booking button disabled while the
   * "different address" checkbox is checked but the invoice-address form is incomplete.
   */
  protected readonly isInvoiceAddressMandatory = isInvoiceAddressMandatoryFor;

  protected onInvoiceAddressChangeEvent(event: { checked: boolean; valid: boolean; value: AppointmentBookItem['invoice_address'] | null }): void {
    this.invoiceAddressChecked = event.checked;
    this.invoiceAddressValid = event.valid;
    this.invoiceAddress = event.value ?? undefined;
  }

  protected get isInvoiceAddressFormBlockingBooking(): boolean {
    return this.invoiceAddressChecked && !this.invoiceAddressValid;
  }

  protected onCouponFailedEvent(): void {
    this.coupon = undefined;
    this.finalCouponDiscount = 0;
    this.applyNetAfterCoupon(this.totalCartPrice);
    this.customEventService.couponChangeEvent.emit(0);
  }

  private calculateCoupon(): void {
    if (this.coupon && this.coupon.is_fixed_amount === 1) {
      this.finalCouponDiscount = Number(this.coupon.fixed_amount);
    }

    if (this.coupon && this.coupon.is_percentage === 1) {
      this.finalCouponDiscount = Number(((Number(this.totalCartPrice) / 100) * this.coupon.percentage).toFixed(2));
    }

    this.applyNetAfterCoupon(Number(this.totalCartPrice) - Number(this.finalCouponDiscount));
  }

  // priceAfterCouponReduction carries the GROSS amount: cw-payment-methods and the Stripe
  // PaymentIntent use it as the amount to charge.
  private applyNetAfterCoupon(netAfterCoupon: number): void {
    const vat = this.calculateCartVat(netAfterCoupon);
    this.cartVat.set(vat);
    this.priceAfterCouponReduction = Number((Number(netAfterCoupon) + vat.totalCents / 100).toFixed(2));
  }

  // Summed in whole cents: 100.10 + 7.60 in floating point is 107.69999999999999, which a
  // payment provider that truncates would charge as 107.69.
  protected cartTotalInclVat(): number {
    if (!(Number(this.totalCartPrice) > 0)) {
      return this.totalCartPrice;
    }
    return (Math.round(Number(this.totalCartPrice) * 100) + this.cartVat().totalCents) / 100;
  }

  /**
   * VAT already contained in the price for `inclusive` partners, on the price after the coupon —
   * display only, recomputed from the restored state so it survives a payment return.
   */
  protected containedVat(): VatResult {
    return this.calculateCartVat(Number(this.totalCartPrice) - Number(this.finalCouponDiscount || 0), true);
  }

  protected subtotalLabelKey(): string {
    return this.showsVatRows() ? 'tax_info.net_total' : 'summary_page_translations.subtotal';
  }

  protected totalLabelKey(): string {
    if (vatTypeLabelKey(this.partner?.widget_vat_type) === 'exclude') {
      return 'tax_info.amount_excl_tax';
    }
    return this.showsVatRows() ? 'tax_info.amount_incl_tax' : 'appointments.step4.total';
  }

  /** The VAT wording only applies when VAT is added on top or the prices actually contain some. */
  private showsVatRows(): boolean {
    return isVatAddedOnTop(this.partner?.widget_vat_type) || this.containedVat().byRate.length > 0;
  }

  private calculateCartVat(discountedNet: number, contained = false): VatResult {
    const vatType = this.partner?.widget_vat_type;
    if (contained ? vatTypeLabelKey(vatType) !== 'inclusive' : !isVatAddedOnTop(vatType)) {
      return { byRate: [], totalCents: 0 };
    }
    const price = Number(this.selectedService?.price);
    const rate = Number(this.selectedService?.tax?.rate);
    const lines: TaxableLine[] = price > 0 && rate > 0
      ? [{ netCents: Math.round(price * 100), rateHundredths: Math.round(rate * 100) }]
      : [];
    // cake caps the coupon at the price, so a larger fixed coupon leaves no VAT rather than a negative one
    return (contained ? calculateIncludedVat : calculateVat)(lines, Math.max(0, Math.round(discountedNet * 100)), Math.round(Number(this.totalCartPrice) * 100));
  }

  protected onStoreBookingItemsEvent(): void {
    if (this.paymentType === this.paymentTypes.CREDITCARD && !this.stripePaymentMethodId) {
      LoggerService.log('[PreDefinedSlots] Storing booking state before Stripe redirect');

      const data = this.buildRedirectionSnapshot();

      this.utilService.updateLocalStorage(APPOINTMENT_CONSTANT.PDS_STRIPE_REDIRECTION_DATA_LS_NAME, data);
      this.storageComplete = true;
    }
  }

  /** The booking as it stands, for a payment return (Stripe redirect, Adyen resume) to book as is. */
  private buildRedirectionSnapshot(): Record<string, unknown> {
    return {
      selectedService: this.selectedService,
      selectedSlot: this.selectedSlot,
      customFieldValues: this.customFieldValues,
      userState: this.userState,
      totalCartPrice: this.totalCartPrice,
      coupon: this.coupon,
      finalCouponDiscount: this.finalCouponDiscount,
      priceAfterCouponReduction: this.priceAfterCouponReduction,
      cartVat: this.cartVat(),
      paymentType: this.paymentType,
    };
  }

  /** Puts the booking a payment return parked back in place (Stripe redirect, Adyen resume). */
  private restoreRedirectionSnapshot(data: Record<string, any>): void {
    this.selectedService = data.selectedService;
    this.selectedSlot = data.selectedSlot;
    this.customFieldValues = data.customFieldValues || {};
    this.userState = data.userState || ({} as UserState);
    this.totalCartPrice = data.totalCartPrice || 0;
    this.coupon = data.coupon;
    this.finalCouponDiscount = data.finalCouponDiscount || 0;
    this.priceAfterCouponReduction = data.priceAfterCouponReduction;
    this.cartVat.set(data.cartVat ?? { byRate: [], totalCents: 0 });
  }

  /**
   * The shopper comes back from a page an Adyen payment had to leave for (a 3DS redirect, or the
   * hosted page in this tab). Restores the booking exactly as it was parked, lets AdyenResumeService
   * finish the payment, and books once -
   * the same stored booking a Stripe redirect return books, with the Adyen handles in place of the
   * payment intent. `/book` still verifies the payment with Adyen itself.
   */
  private resumeAdyenPayment(resume: AdyenResumeReturn): void {
    const data = resume.record.snapshot.data as Record<string, any>;
    this.restoreRedirectionSnapshot(data);
    this.currentStep = PreDefinedSlotStep.PERSONAL_FORM;
    this.bookingInProgress = true;
    this.bookingErrorMessage = undefined;
    this.showStripeRedirectionLoader = true;
    this.paymentType = this.paymentTypes.ADYEN;

    this.adyenResumeService.complete(resume, {
      holdScope: buildAdyenHoldScope({
        widgetType: this.appointmentConstant.APPOINTMENT,
        cart: this.appointmentCartForMeetingTypes,
        predefinedSlotUuid: this.selectedSlot?.uuid,
      }),
      items: this.appointmentCartForMeetingTypes?.length ?? 0,
    }).then(details => {
      this.adyenPaymentDetails = details;
      this.doBooking();
    }, (failure: AdyenResumeFailure) => {
      // The booking stays restored on the payment step; paying again mints a fresh session unless
      // the payment is held (Pending), which cw-adyen then says itself.
      this.showStripeRedirectionLoader = false;
      this.bookingInProgress = false;
      // The resume jumped straight to the personal form, past the slot step that loads the payment
      // settings - without them the payment methods stay hidden and Book would send an unpaid
      // Adyen booking. Load them, as a failed Stripe return does, so the form can be paid again.
      this.verifyOnlinePaymentStatus();
      // The services list is what Back goes to from here.
      this.loadServices();
      this.bookingErrorMessage = failure?.silent
        ? undefined
        : this.translate.instant(failure?.messageKey ?? 'adyen.errors.unexpected');
    });
  }

  private markFormGroupTouched(): void {
    if (!this.preDefinedSlotForm) return;
    Object.values(this.preDefinedSlotForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  private resetPaymentAndCouponState(): void {
    this.coupon = undefined;
    this.finalCouponDiscount = 0;
    this.priceAfterCouponReduction = undefined;
    this.cartVat.set({ byRate: [], totalCents: 0 });
    this.totalCartPrice = 0;
    this.paymentType = undefined;
    this.stripePaymentMethodId = null;
    this.refundTransactionId = null;
    this.showRefundNotice = false;
    this.paypalOrderDetail = undefined;
    this.saferPayPaymentDetails = undefined;
    this.adyenPaymentDetails = undefined;
    this.isPaymentInProgress = false;
    this.storageComplete = false;
    this.showFullscreenOverlay = false;
    this.bookingErrorMessage = undefined;
  }

  protected isPaymentRequired(): boolean {
    return this.totalCartPrice > 0 && this.isServiceOnlinePaymentEnabled === 1;
  }

  private isPaymentBookButtonValidated(): boolean {
    if (this.totalCartPrice <= 0 || this.priceAfterCouponReduction <= 0) {
      this.paymentType = this.paymentTypes.STORE;
      return true;
    }
    if (
      !this.isStripeEnabled && !this.isPaypalEnabled && !this.isSaferpayEnabled && !this.isAdyenEnabled &&
      !this.isLexOfficePaymentTypeEnabled && !this.isExternalInvoiceEnabled
    ) {
      this.paymentType = this.paymentTypes.STORE;
      return true;
    }
    if (this.isOnlinePaymentMandatory) {
      const isValid = !!(
        (this.stripePaymentMethodId && this.paymentType === this.paymentTypes.CREDITCARD) ||
        this.paymentType === this.paymentTypes.INVOICE ||
        this.paymentType === this.paymentTypes.LEX_OFFICE_INVOICE ||
        this.paymentType === this.paymentTypes.EXTERNAL_INVOICE ||
        (this.paypalOrderDetail && this.paymentType === this.paymentTypes.PAYPAL) ||
        (this.saferPayPaymentDetails && this.paymentType === this.paymentTypes.SAFERPAY) ||
        (this.adyenPaymentDetails && this.paymentType === this.paymentTypes.ADYEN)
      );
      if (!isValid) {
        this.customEventService.paymentGatewayErrorEvent.emit(true);
      }
      return isValid;
    }
    // Optional payment still cannot book "paid online" with no online payment: /book would carry
    // an online payment_type and nothing behind it, and the backend saves that as a payment that
    // never happened. Same answer the appointment, event and compact surfaces give, for every
    // online method this surface offers.
    const unpaidOnlineMethod =
      (this.paymentType === this.paymentTypes.ADYEN && !this.adyenPaymentDetails) ||
      (this.paymentType === this.paymentTypes.PAYPAL && !this.paypalOrderDetail) ||
      (this.paymentType === this.paymentTypes.SAFERPAY && !this.saferPayPaymentDetails);
    if (unpaidOnlineMethod) {
      this.customEventService.paymentGatewayErrorEvent.emit(true);
      return false;
    }
    return true;
  }

  private tryAutoSelectService(): void {
    if (!this.selectedAppointmentServiceIds) {
      return;
    }
    const ids = Array.isArray(this.selectedAppointmentServiceIds)
      ? this.selectedAppointmentServiceIds.map(Number)
      : [Number(this.selectedAppointmentServiceIds)];

    for (const category of this.serviceCategories) {
      const match = category.services?.find(s => ids.includes(s.id));
      if (match) {
        this.selectedService = match;
        this.onNextFromServiceSelection();
        return;
      }
    }
  }

  protected isNextDisabled(): boolean {
    return !this.selectedSlot;
  }

  protected switchLanguage(language: string): void {
    this.lang = language;
    this.translate.use(language);
    this.globals.user_selected_language = language;
  }

  private scrollToComponent(): void {
    if (this.widgetConf?.scroll_to_element_feature_is_enabled === 1) {
      const element = getDomRoot(this.elementRef.nativeElement).getElementById('pds-component');
      element?.scrollIntoView();
    }
  }
}
